// Comprehensive error logging for PWA debugging
export class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 50;
    this.setupGlobalHandlers();
  }

  setupGlobalHandlers() {
    // Catch unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError('JavaScript Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError('Unhandled Promise Rejection', {
        reason: event.reason,
        promise: event.promise,
        stack: event.reason?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Service Worker errors
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('error', (event) => {
        this.logError('Service Worker Error', {
          message: event.message || 'SW Error',
          filename: event.filename,
          lineno: event.lineno,
          timestamp: new Date().toISOString()
        });
      });
    }
  }

  logError(type, details) {
    const errorEntry = {
      id: Date.now() + Math.random(),
      type,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    };

    this.errors.unshift(errorEntry);
    
    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Store in localStorage for persistence
    try {
      localStorage.setItem('papersedge-errors', JSON.stringify(this.errors));
    } catch (e) {
      console.warn('Could not save errors to localStorage:', e);
    }

    // Also log to console for immediate debugging
    console.error(`[${type}]`, details);
  }

  getErrors() {
    return this.errors;
  }

  clearErrors() {
    this.errors = [];
    try {
      localStorage.removeItem('papersedge-errors');
    } catch (e) {
      console.warn('Could not clear errors from localStorage:', e);
    }
  }

  // Get errors from localStorage (for when app restarts)
  loadStoredErrors() {
    try {
      const stored = localStorage.getItem('papersedge-errors');
      if (stored) {
        this.errors = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Could not load stored errors:', e);
    }
  }

  // Export errors for sharing/debugging
  exportErrors() {
    return {
      errors: this.errors,
      environment: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        localStorage: this.getLocalStorageInfo(),
        serviceWorker: this.getServiceWorkerInfo()
      }
    };
  }

  getLocalStorageInfo() {
    try {
      const keys = Object.keys(localStorage);
      return {
        keys: keys,
        papersedgeAuth: !!localStorage.getItem('papersedge-auth'),
        supabaseAuth: !!localStorage.getItem('sb-jjkfasielosxogfccptn-auth-token'),
        customStorageKey: !!localStorage.getItem('papersedge-auth')
      };
    } catch (e) {
      return { error: e.message };
    }
  }

  getServiceWorkerInfo() {
    if (!('serviceWorker' in navigator)) {
      return { supported: false };
    }

    return {
      supported: true,
      controller: !!navigator.serviceWorker.controller,
      ready: navigator.serviceWorker.ready?.then ? 'pending' : 'ready'
    };
  }
}

// Create global instance
export const errorLogger = new ErrorLogger();

// Load any stored errors on startup
errorLogger.loadStoredErrors();